import Events from 'events';
import { cloneDeep } from 'lodash';
import { Replayer, IncrementalSource, ReplayerEvents } from 'sb-lib/rrweb-lib';
import {
  ANY_RID,
  fullSnapshotEvent,
  Handler,
  metaEvent,
  playerConfig,
  Rid,
  SbRrwebEvents,
  RrwebEventMetaMode,
  eventWithTime,
  EventType,
} from 'sb-lib/types';
import { makeLog } from './log';

const log = makeLog('sb-rrweb');

const throttledLogDebug = ((ms: number) => {
  let lastTime = 0;
  return (msg: string) => {
    if (Date.now() - lastTime > ms) {
      lastTime = Date.now();
      log.debug(`${msg} [throttled log ${ms}ms]`);
    }
  };
})(5000);

type BoundaryEventsMapMetaEvent = metaEvent & { timestamp: number };
type BoundaryEventsMapSnapshotEvent = fullSnapshotEvent & { timestamp: number };
type BoundaryEventsMap = {
  [key: string]: {
    mode: RrwebEventMetaMode;
    time: number;
    meta: BoundaryEventsMapMetaEvent | null;
    snapshot: BoundaryEventsMapSnapshotEvent | null;
  };
};

export type SbReplayerEvent = 'mode' | 'start' | 'stop' | 'status' | 'snapshot';
export type SbReplayerStatus = 'idle' | 'waiting' | 'active';

interface SbReplayerEmitter extends Events.EventEmitter {
  on(event: SbReplayerEvent, listener: (...args: any[]) => void): this;
  emit(event: SbReplayerEvent, ...args: any[]): boolean;
}

export class SbReplayer extends Replayer {
  static RRWEB_REPLAYER_BUFFER = 350;
  #snapshotReceivedTime = 0;
  #snapshotAddedTime = 0;
  #checkRecordingReadyTimeout = 0;
  #emitter = new Events.EventEmitter() as SbReplayerEmitter;

  #earlyEvents = {} as { [key: string]: eventWithTime[] };
  #boundaryEvents = {} as BoundaryEventsMap;
  #staleRecordingIds = [] as string[];
  #currentRecordingId = '';
  #currentRecordingMode = null as null | RrwebEventMetaMode;
  #status = 'idle' as SbReplayerStatus;
  #lastTimestamp = 0;
  #lastRealTimestamp = 0;

  constructor(
    readonly name: string,
    events: eventWithTime[],
    config: Partial<playerConfig>,
  ) {
    super(events, config);
    this.on(ReplayerEvents.FullsnapshotRebuilded, () => {
      const duration = Date.now() - this.#snapshotReceivedTime;
      log.debug(
        `[${name}] Rid ${this.#currentRecordingId} snapshot built in ${duration}ms`,
      );
    });
  }
  addEvent(event: eventWithTime, rid?: Rid, mode?: RrwebEventMetaMode) {
    if (!rid) {
      log.warn(`[${this.name}] SbReplayer.addEvent requires a recording id`);
      return;
    }

    // If special rid is set, just clone active rid, or ignore
    if (rid === ANY_RID) {
      if (this.#currentRecordingId) {
        rid = this.#currentRecordingId;
      } else {
        return;
      }
    }

    const isStaleRid = this.#hasStaleRid(rid);
    const isRidChange =
      !isStaleRid && this.#currentRecordingId && this.#currentRecordingId !== rid;

    // Old rid, ignore event
    if (this.#currentRecordingId && isStaleRid) {
      log.debug(
        `[${this.name}] Ignoring old event from rid ${rid}, current rid ${
          this.#currentRecordingId
        }`,
      );
      return;
    }

    const addEvent = (ev: eventWithTime) => {
      const isSnapshot = isRrwebSnapshot(ev);
      if (isSnapshot) this.#snapshotAddedTime = Date.now();
      let timestamp = Date.now() + SbReplayer.RRWEB_REPLAYER_BUFFER;
      const diffFromSnapshot =
        timestamp - (this.#snapshotAddedTime + SbReplayer.RRWEB_REPLAYER_BUFFER);
      if (!isSnapshot && diffFromSnapshot < 1) {
        timestamp = this.#snapshotAddedTime + SbReplayer.RRWEB_REPLAYER_BUFFER + 1;
      }

      // make relative timestamp adjustments when the real times are close
      // this helps animation playback when the events are batched
      const realTimestampDiff = ev.timestamp - this.#lastRealTimestamp;
      this.#lastRealTimestamp = ev.timestamp;
      if (realTimestampDiff < 100) {
        timestamp = this.#lastTimestamp + realTimestampDiff;
        const drift = timestamp - Date.now();
        if (drift > 500) {
          timestamp = this.#lastTimestamp + Math.round(realTimestampDiff / 2);
          throttledLogDebug(
            `Drifted ${drift}ms, halving to ${timestamp - Date.now()}ms.`,
          );
        }
      }

      // make sure each timestamp has at least a 1 ms diff
      if (timestamp - this.#lastTimestamp < 1) {
        timestamp = this.#lastTimestamp + 1;
      }

      this.#lastTimestamp = timestamp;
      super.addEvent({ ...ev, timestamp });
    };

    // Currently playing, just add the event to rrweb
    if (this.#currentRecordingId && !isRidChange) {
      addEvent(event);
      return;
    }

    // New rid detected, reset and wait for meta and snapshot
    if (isRidChange) {
      log.debug(
        `[${this.name}] New recording id encountered ${rid}, current ${
          this.#currentRecordingId
        }`,
      );
      this.#addStaleRid(this.#currentRecordingId);
      this.#emitter.emit('stop', this.#currentRecordingId);
      this.#setStatus('waiting');
      this.#currentRecordingId = '';
      clearTimeout(this.#checkRecordingReadyTimeout);
      this.#checkRecordingReadyTimeout = window.setTimeout(() => {
        // TODO - emit event and send a restart trigger to widget
        log.warn(
          `[${this.name}] Still waiting for boundary events after 5 seconds, rid ${rid}`,
        );
      }, 5000);
    }

    // Waiting for meta and snapshot, buffer events
    this.#bufferEvent(event, rid, mode);

    // Boundary events ready, start playing
    const readyEvents = this.#getEventsWhenReady();
    if (readyEvents) {
      log.debug(
        `[${this.name}] Boundary events for rid ${readyEvents.rid} found, will play`,
      );
      clearTimeout(this.#checkRecordingReadyTimeout);
      this.#snapshotReceivedTime = readyEvents.boundaryEvents.time;
      this.#currentRecordingId = readyEvents.rid;
      if (this.#currentRecordingMode != readyEvents.boundaryEvents.mode) {
        this.#emitter.emit('mode', readyEvents.boundaryEvents.mode);
      }
      this.#currentRecordingMode = readyEvents.boundaryEvents.mode;

      super.startLive(Date.now());
      this.#emitter.emit('start', readyEvents.rid);
      this.#setStatus('active');
      addEvent(readyEvents.boundaryEvents.meta as eventWithTime);
      addEvent(readyEvents.boundaryEvents.snapshot as eventWithTime);
      if (readyEvents.earlyEvents.length) {
        log.debug(
          `[${this.name}] Playing ${readyEvents.earlyEvents.length} early events, rid ${readyEvents.rid}`,
        );
      }
      readyEvents.earlyEvents.forEach(addEvent);
    }
  }

  onSnapshotBuilt(cb: Handler) {
    this.on(ReplayerEvents.FullsnapshotRebuilded, cb);
  }

  clearReplayer() {
    try {
      this.timer.toggleLiveMode(false);
      this.timer.clear();
      this.pause();
      clearTimeout(this.#checkRecordingReadyTimeout);
    } catch (err) {
      log.debug(`[${this.name}] Could not clear Replayer`, err);
    }
  }

  getIframeBody() {
    return this.iframe.contentDocument?.body;
  }

  #bufferEvent(event: eventWithTime, rid: Rid, mode?: RrwebEventMetaMode) {
    this.#boundaryEvents[rid] = this.#boundaryEvents[rid] || {
      time: 0,
      meta: null,
      snapshot: null,
    };
    this.#earlyEvents[rid] = this.#earlyEvents[rid] || [];
    if (isRrwebMeta(event)) {
      this.#boundaryEvents[rid].meta = event as BoundaryEventsMapMetaEvent;
    } else if (isRrwebSnapshot(event)) {
      if (!mode) {
        mode = this.#currentRecordingMode || 'single';
        log.warn(`[${this.name}] No mode set for snapshot event, will set to "${mode}"`, {
          event,
          rid,
        });
      }
      this.#boundaryEvents[rid].snapshot = event as BoundaryEventsMapSnapshotEvent;
      this.#boundaryEvents[rid].time = Date.now();
      this.#boundaryEvents[rid].mode = mode;
      this.#emitter.emit('snapshot');
    } else if (isRrwebIncremental(event)) {
      this.#earlyEvents[rid].push(event);
    }
  }

  #getEventsWhenReady() {
    for (const [rid, bes] of Object.entries(this.#boundaryEvents)) {
      if (bes.snapshot && bes.meta) {
        const initialEvents = {
          rid,
          boundaryEvents: bes,
          earlyEvents: this.#earlyEvents[rid] || [],
        };
        this.#boundaryEvents = {};
        this.#earlyEvents = {};
        return initialEvents;
      }
    }
    return null;
  }

  #addStaleRid(rid: string) {
    // Just check against the last three
    if (this.#staleRecordingIds.length === 3) {
      this.#staleRecordingIds.pop();
    }
    this.#staleRecordingIds.push(rid);
  }

  #hasStaleRid(rid: string) {
    return this.#staleRecordingIds.includes(rid);
  }

  #setStatus(status: SbReplayerStatus) {
    this.#status = status;
    this.#emitter.emit('status', status);
  }

  getStatus() {
    return this.#status;
  }

  sbOn(eventName: SbReplayerEvent, callback: (...args: any[]) => void) {
    this.#emitter.on(eventName, callback);
  }
}

export const isRrwebMeta = (event: SbRrwebEvents | eventWithTime) => {
  const isM = (x: eventWithTime) => x.type === EventType.Meta;
  if ((event as any).events) {
    return !!(event as SbRrwebEvents).events.reduce((a, x) => isM(x) || a, false);
  } else {
    return isM(event as eventWithTime);
  }
};

export const isRrwebSnapshot = (event: SbRrwebEvents | eventWithTime) => {
  const isSS = (x: eventWithTime) => x.type === EventType.FullSnapshot;
  if ((event as any).events) {
    return !!(event as SbRrwebEvents).events.reduce((a, x) => isSS(x) || a, false);
  } else {
    return isSS(event as eventWithTime);
  }
};

export const isRrwebIncremental = (event: SbRrwebEvents | eventWithTime) => {
  const isI = (x: eventWithTime) => x.type === EventType.IncrementalSnapshot;
  if ((event as any).events) {
    return (event as SbRrwebEvents).events.reduce((a, x) => isI(x) || a, false);
  } else {
    return isI(event as eventWithTime);
  }
};

export const isRrwebMouseMoveEvent = (event: SbRrwebEvents | eventWithTime) => {
  const isMM = (x: eventWithTime) =>
    x.type === EventType.IncrementalSnapshot &&
    x.data.source === IncrementalSource.MouseMove;
  if ((event as any).events) {
    return (event as SbRrwebEvents).events.reduce((a, x) => isMM(x) || a, false);
  } else {
    return isMM(event as eventWithTime);
  }
};

export const cloneRrwebMouseEventForWidget = (event: SbRrwebEvents) => {
  const eventClone = cloneDeep(event);
  eventClone.type = 'rrwebWidgetEvent';
  eventClone.rid = ANY_RID;
  return eventClone;
};
export const massageClonedRrwebMouseEventsForWidget = (event: SbRrwebEvents) => {
  event.events.forEach(re => {
    if (
      re.type === EventType.IncrementalSnapshot &&
      re.data.source === IncrementalSource.MouseMove
    ) {
      // set all position ids to 1 so they don't try to hover on anything specific
      re.data.positions.forEach(p => (p.id = 1));
    }
  });
};
